import { PromoCode, SimpleOrganisation } from "./Cart";
import { Addon, TicketType } from "./Tickets";

export enum OrderStatus {
  PENDING = "PENDING",
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
}

export enum AttendeeStatus {
  VALID = "VALID",
  NOT_YET_VALID = "NOT_YET_VALID",
}

export interface PaginationBase {
  numPages: number;
  count: number;
  perPage: number;
  page: number;
}

export interface PaginatedResponse<T> extends PaginationBase {
  results: Array<T>;
}

export interface Base {
  id: number;
  uuid: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Event extends Base {
  name: string;
  location: string;
  startDate: Date;
  endDate: Date;
  active: boolean;
  slug: string;
  logo: string;
  showEventDetails: boolean;
  organisation?: SimpleOrganisation;
  platformFee?: string;
  platformFeePercentage?: string;
}

export interface Organisation extends Base {
  name: string;
  organisationViewEnabled: boolean;
  events: Array<Event>;
  description?: string;
  domain: string;
  logo?: string;
  ticketingEnabled: boolean;
  accreditationEmailsEnabled: boolean;
  accreditationGroupsEnabled: boolean;
  formsEnabled: boolean;
  flowsEnabled: boolean;
}

export interface DashboardTicket extends Base {
  name: string;
  price: string;
  active: boolean;
  ticketType: TicketType;
  fee: string;
  numberOfMonths?: number;
  productId?: string;
  perMonthPrice?: string;
  onSaleFrom: Date;
  onSaleTo: Date;
  quantityAvailable: number;
  quantitySold: number;
  hidden: boolean;
  isFree: boolean;
  isSoldOut: boolean;
  isTicketOnSale: boolean;
  ticketOptionsRange: number;
}

export interface PurchasedAddonInstance {
  uuid: string;
  status: string;
  addon: Addon;
  checkedIn: boolean;
  checkedInAt: Date;
}

export interface PurchasedAddon {
  total: string;
  feeTotal: string;
  addons: Array<PurchasedAddonInstance>;
}

export interface TicketQuestion {
  uuid: string;
  question: string;
}

export interface TicketAnswer {
  uuid: string;
  answer: string;
  question: TicketQuestion;
}

export interface InvoicePayment {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  uuid: string;
  invoiceId: string;
  total: string;
  order: number;
}

export interface DashboardOrder extends Base {
  firstName: string;
  lastName: string;
  emailAddress: string;
  status: OrderStatus;
  total: string;
  monthlyTotal?: string;
  fees: string;
  carbonFootprintContribution: string;
  gatewayId: string;
  subscriptionId?: string;
  orderType: string;
  paymentCompletedAt: Date;
  cardBrand?: string;
  cardLast4?: string;
  promoCode?: PromoCode;
  attendees: Array<DashboardAttendee>;
  purchasedAddons: PurchasedAddon;
  ticketAnswers: Array<TicketAnswer>;
  invoicePayments?: Array<InvoicePayment>;
}

export interface RecentOrder {
  date: Date;
  orders: Array<DashboardOrder>;
}

export interface DashboardAttendee extends Base {
  status: AttendeeStatus;
  checkedIn: boolean;
  checkedInAt: Date;
  order: number;
  ticket: DashboardTicket;
  event: Event;
}

export interface DashboardOrderResponse {
  order: DashboardOrder;
  attendeesNumPages: number;
  attendeesCount: number;
  attendees: Array<DashboardAttendee>;
  attendeesPerPage: number;
  attendeesPage: number;
  event: Event;
}

export interface SaleCount {
  tickets: number;
  addons: number;
}

export interface SalesCounts {
  daily: SaleCount;
  weekly: SaleCount;
}

export interface Statistics {
  grossTotal: number;
  netTotal: number;
  attendeesCheckedIn: number;
  totalRange: Array<number>;
  carbonFootprintContributions: number;
  paymentPlanCarbonFootprintContributions: number;
  feesTotal: number;
  paymentPlansComplete: number;
  ticketsSold: number;
  ticketsIssued: number;
  ticketsPending: number;
  ticketsFailed: number;
  freeTicketsSold: number;
  ticketsSoldOnEventbrite: number;
  netSalesInLast24Hours: number;
  grossSalesInLast24Hours: number;
  paymentPlanSales: number;
  outstandingPlanSales: number;
  event: Event;
  salesCounts: SalesCounts;
  addonSales: number;
  addonCount: number;
}

export interface Attendee extends Base {
  ticket: DashboardTicket;
  order: DashboardOrder;
  status: AttendeeStatus;
  checkedIn: boolean;
  checkedInAt?: Date;
  event: Event;
}

export interface DropdownOption {
  id: number | string;
  value: string;
  // optional label below the option to describe the option in more detail
  label?: string;
}

export interface SignInRequest {
  username: string;
  password: string;
}

export interface SignInResponse {
  access: string;
  refresh: string;
}

export interface RefreshResponse {
  access: string;
}

export interface User {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface UpdatePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

export interface EmailRecipient {
  Email: string;
  Name: string;
}

export interface EmailMessage {
  MessageID: string;
  MessageStream: string;
  To: EmailRecipient[];
  Cc: EmailRecipient[];
  Bcc: EmailRecipient[];
  Recipients: string[];
  ReceivedAt: string;
  From: string;
  Subject: string;
  Attachments: string[];
  Status: string;
  TrackOpens: boolean;
  TrackLinks: string;
  Metadata: Record<string, any>;
  Sandboxed: boolean;
}

export interface Permission {
  id: number;
  name: string;
  codename: string;
  contentType: number;
}

export interface Group {
  id: number;
  name: string;
  permissions: Array<Permission>;
}

export interface OrganisationUser {
  organisation: Organisation;
  isLoggedIn: boolean;
  status: string;
  user: User;
}
